<script setup lang="ts">
import { IconExclamationCircle } from '@tabler/icons-vue'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'
import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import { Event } from '@tix/entity'
import { isNotFoundError } from '@tix/openapi-sdk'

import { setDocumentTitle } from '@/helpers/documentTitle'
import { buildDonationCustomAmount, buildDonationPresets } from '@/helpers/donationHelpers'
import { emptyDonationOptionsValues, getDonationOptionsVisibility } from '@/helpers/donationSurvey'
import { formatEventGalleryImages } from '@/helpers/imageHelpers'
import { buildDonationsPaymentReturnUrl } from '@/helpers/paymentReturn'

import { errorTracker } from '@/services/errorTracking'

import { useGlobalError } from '@/stores/useGlobalErrorStore'

import CheckoutIdentityFields from '@/components/CheckoutIdentityFields.vue'
import ImageGallery from '@/components/ImageGallery.vue'
import DonationCartTotal from '@/components/donations/DonationCartTotal.vue'
import DonationHeader from '@/components/donations/DonationHeader.vue'
import DonationOptions from '@/components/donations/DonationOptions.vue'
import DonationWidget from '@/components/donations/DonationWidget.vue'
import PaymentComponent from '@/components/donations/PaymentComponent.vue'

import { useDonationCart } from '@/composables/useDonationCart'
import { useDonationGa4Tracking } from '@/composables/useDonationGa4Tracking'

const props = defineProps<{
  id?: string
}>()

const router = useRouter()
const globalError = useGlobalError()

const donationOptionsRef = ref<InstanceType<typeof DonationOptions> | null>(null)
const donationOptions = ref(emptyDonationOptionsValues())

const eventTemplateId = props.id

const result = eventTemplateId ? await Event.load(eventTemplateId) : null

const event = result?.data ?? null
const eventTemplate = event?.data ?? null
const ticketGroups = event?.ticketGroups ?? []
const ticketTypes = event?.ticketTypes ?? []
const error = result?.error ?? null

// TODO: Implement global error-handling for all routes
if (!eventTemplate) {
  if (!eventTemplateId || isNotFoundError(error)) {
    globalError.set('Not found', { dismissable: false })
  } else {
    errorTracker.track(new Error('Error fetching donation event', { cause: error }), {
      operation: 'donation.event.get',
      eventTemplateId,
    })
    globalError.set('An unexpected error occurred', { dismissable: false })
  }
}

const donationGroup = ticketGroups.find((group) => group.handler === 'donation')
const donationTicketTypes = ticketTypes.filter((ticketType) => ticketType.ticket_group_id === donationGroup?.id)

const title = donationGroup?.name ?? eventTemplate?.name ?? ''
const summary = donationGroup?.summary
const description = donationGroup?.description
const images = event ? formatEventGalleryImages(event.metadata) : []
const presetAmounts = buildDonationPresets(donationTicketTypes)
const customAmount = buildDonationCustomAmount(donationTicketTypes)

const hasDonationAmountOptions = presetAmounts.length > 0 || customAmount !== null

// The event loaded fine, but without a donation group or valid amounts there's
// nothing for the user to donate to. Treat it as not found rather than showing
// a header/gallery with no way to donate.
if (eventTemplate && !hasDonationAmountOptions) {
  globalError.set('Not found', { dismissable: false })
}

if (title) setDocumentTitle(title)

const donationOptionsVisibility = getDonationOptionsVisibility(donationGroup?.additional_info_spec)

const {
  cart,
  pending,
  error: cartError,
  selection,
  onSelectPreset,
  onCustomAmountUpdate,
  checkoutCart,
  checkoutPreflight,
} = useDonationCart(
  customAmount,
  donationOptionsVisibility && donationGroup ? { donationOptions, donationGroup, donationOptionsRef } : undefined,
)

const eventReady = Boolean(eventTemplate) && hasDonationAmountOptions
const paymentReady = computed(() => eventReady && Boolean(cart.value?.stripeFee))
const isCartGone = computed(() => cartError.value === 'cart-gone')
/** True while StripePayment checkout is in flight — freezes the amount widget. */
const paymentSubmitting = ref(false)
// TODO: update label and icon in TIC-6842
const paymentButtonLabel = 'Pay'
const payButtonIcon = undefined
/** Set when Pay is clicked before a reserved amount. Cleared once Stripe can mount. */
const amountInvalid = ref(false)
const preStripeError = ref<string | null>(null)

watch(paymentReady, (ready) => {
  if (!ready) return
  amountInvalid.value = false
  preStripeError.value = null
})

const { viewItem, beginCheckout, trackPurchase } = useDonationGa4Tracking({
  eventTemplate,
  donationGroup: donationGroup ?? null,
  ticketTypes: donationTicketTypes,
  paymentReady,
  cart,
  donationOptions,
})
viewItem()

function handlePaymentComplete(data: { email: string; order: string; amount: number }) {
  trackPurchase(data.order)
  router.push({
    name: 'donation-complete',
    params: { id: eventTemplateId },
    query: {
      email: data.email,
      order: data.order,
      // PaymentComponent emits Stripe minor units; the complete route/PriceDisplay expect major units.
      amount: String(data.amount / 100),
    },
  })
}

// Non-null assertion is safe: CheckoutIdentityFields mounts unconditionally inside `eventReady`, while PaymentComponent only appears after `paymentReady`, which requires user interaction to create a cart with stripeFee.
const checkoutIdentity = useTemplateRef<InstanceType<typeof CheckoutIdentityFields>>('checkoutIdentity')

async function onPayWithoutPayment() {
  const identity = checkoutIdentity.value?.getValidatedIdentity()
  if (!identity) return

  const noAmount = !cart.value?.tickets?.[0]
  amountInvalid.value = noAmount

  // TODO: render validation errors as bulleted list in TIC-6803
  if (identity.status === 'error') {
    // we know that the identity error messages start with a number
    // parseInt will ignore an invalid character and all succeeding characters
    const incrementedErrorCount = parseInt(identity.error) + 1

    // identity only errors when at least one field fails, so it's always plural after adding 1
    preStripeError.value = noAmount ? `${incrementedErrorCount} fields need your attention.` : identity.error
    return
  }

  // run remaining validation not handled by identity
  const preflight = await checkoutPreflight()
  if (preflight.status === 'error') {
    preStripeError.value = preflight.error.message
    return
  }

  amountInvalid.value = false
  preStripeError.value = null
}

/** Destination paths only. Cart id and receipt email are layered at Stripe confirm. */
const returnUrl = computed(() => buildDonationsPaymentReturnUrl(eventTemplateId!))
</script>

<template>
  <div v-if="eventReady" class="donation-page">
    <ImageGallery v-if="images.length" class="donation-page-gallery" :images="images" />

    <div class="donation-page-header">
      <DonationHeader :description="description" :name="title" :summary="summary" />
    </div>

    <aside class="donation-page-widget">
      <DonationWidget
        :customAmount="customAmount"
        :disabled="paymentSubmitting"
        :invalid="amountInvalid"
        :presetAmounts="presetAmounts"
        :selection="selection"
        @selectPreset="onSelectPreset"
        @update:customAmount="onCustomAmountUpdate"
      />
    </aside>

    <div class="donation-page-details">
      <DonationOptions
        v-if="donationOptionsVisibility"
        ref="donationOptionsRef"
        v-model="donationOptions"
        class="donation-page-options"
        :visibility="donationOptionsVisibility"
      />
      <CheckoutIdentityFields ref="checkoutIdentity" :disabled="paymentSubmitting" @focusin="beginCheckout" />
      <DonationCartTotal :cart="cart" class="donation-page-cart-total" :pending="pending" />
    </div>

    <StatusMessage
      v-if="isCartGone"
      :dismissible="false"
      :icon="IconExclamationCircle"
      message="Your donation session expired. Please select an amount again."
      variant="error"
    />

    <div v-else-if="paymentReady" class="donation-page-payment-container" @focusin="beginCheckout">
      <PaymentComponent
        :key="cart!.id"
        :cart="cart!"
        :checkoutCart="checkoutCart"
        :checkoutPreflight="checkoutPreflight"
        :getValidatedIdentity="checkoutIdentity!.getValidatedIdentity"
        :payButtonIcon="payButtonIcon"
        :paymentButtonLabel="paymentButtonLabel"
        :pending="pending"
        :resolveIdentityData="checkoutIdentity!.resolveIdentityData"
        :returnUrl="returnUrl"
        @complete="handlePaymentComplete"
        @focus="beginCheckout"
        @update:submitting="paymentSubmitting = $event"
      />
    </div>

    <!-- Dummy button and status message handles validation before payment component mounts -->
    <div v-else class="donation-page-payment-container">
      <StatusMessage
        v-if="preStripeError"
        :dismissible="false"
        :icon="IconExclamationCircle"
        :message="preStripeError"
        variant="error"
      />
      <BaseButton :iconBefore="payButtonIcon" type="button" @click="onPayWithoutPayment">{{
        paymentButtonLabel
      }}</BaseButton>
    </div>
  </div>
</template>

<style scoped>
.donation-page {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'gallery'
    'header'
    'widget'
    'details'
    'payment';
  gap: var(--space-1-5);
  padding: var(--space-1) var(--space-1) var(--space-2);

  @media (--desktop-experience) {
    grid-template-columns: minmax(0, 1fr) 420px;
    grid-template-areas:
      'gallery gallery'
      'header widget'
      'details widget'
      'payment widget';
    align-items: start;
    padding: var(--space-2);
    column-gap: var(--space-3);
    row-gap: var(--space-2);
  }

  .donation-page-gallery {
    grid-area: gallery;
  }

  .donation-page-header {
    grid-area: header;
  }

  .donation-page-widget {
    grid-area: widget;
  }

  .donation-page-details {
    display: flex;
    flex-direction: column;
    gap: var(--space-1-5);

    @media (--desktop-experience) {
      grid-area: details;
      max-width: 768px;
      gap: var(--space-2);
      padding-top: var(--space-2);
      border-top: var(--divider);
    }
  }

  .donation-page-payment-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    grid-area: payment;

    @media (--desktop-experience) {
      max-width: 768px;
    }
  }
}
</style>
